import {
  DEFAULT_EXTRA_MINUTES,
  DEFAULT_PREFERENCE,
  DEFAULT_RETURN_ROUTING,
  type LocationSource,
  type PlannedPoint,
  type PlanRequest,
  type ReturnRouting,
  type RidingPreferences
} from './contracts.js';

/**
 * Trips (#48): the server stores them, and the web planner and the ride app
 * both edit them. The format and the rules both clients follow are in
 * contracts/trips/README.md; contracts/trips/v1 holds the fixtures the
 * TypeScript and Kotlin versions of these functions are checked against.
 *
 * Every function here is pure. Functions that create ids take them as
 * arguments, so the same input always gives the same trip.
 */

/** A trip has at most this many sub-trips. */
export const MAX_TOUR_SUBTRIPS = 50;
/** Trip names are 1 to 80 characters, with no surrounding spaces. */
export const MAX_TRIP_NAME = 80;
export const MAX_LOCATION_LABEL = 200;
export const MAX_TRIPS_PER_PLANNER = 500;
/** A planner's stored file (trips, plan records, pairings) may not grow beyond this. */
export const MAX_PLANNER_BYTES = 2 * 1024 * 1024;

export const TRIP_ID_PATTERN = /^[A-Za-z0-9_-]{1,48}$/;
export const SUBTRIP_ID_PATTERN = /^sub-[0-9a-f]{8}$/;
export const LOCATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** Ids routePoints gives the endpoints; no stop may use them. */
export const RESERVED_LOCATION_IDS = ['start', 'destination', 'return'] as const;

/** A start, stop or destination. Ids are stable so reordering and replacement keep identity. */
export interface TripLocation {
  id: string;
  label: string;
  lat: number;
  lng: number;
  source: LocationSource;
}

/**
 * One planned ride: the unit of planning and of a phone package, usually a day
 * of a tour. It starts where the previous sub-trip ended; that place is not
 * stored twice (see [subtripStart]).
 */
export interface Subtrip {
  /** `sub-` and eight hex digits; part of the sub-trip's ride-trip id. */
  id: string;
  destination?: TripLocation;
  stops: TripLocation[];
  returnToStart: boolean;
  preferences: RidingPreferences;
  extraMinutes: number;
  returnRouting: ReturnRouting;
}

/** What clients edit and the server stores. Revision and times are kept beside it, not in it. */
export interface Trip {
  id: string;
  name: string;
  /** Where the first sub-trip starts; id `start`. */
  start?: TripLocation;
  /** One or more. */
  subtrips: Subtrip[];
}

const UNTITLED = 'Untitled trip';

export function newTripId(): string {
  return `trip-${crypto.randomUUID()}`;
}

export function newSubtripId(): string {
  return `sub-${crypto.randomUUID().replaceAll('-', '').slice(0, 8)}`;
}

export function newStopId(): string {
  return `stop-${crypto.randomUUID()}`;
}

/** An empty sub-trip with the default settings, or [settings] copied from another. */
export function newSubtrip(id: string, settings?: Pick<Subtrip, 'preferences' | 'extraMinutes' | 'returnRouting'>): Subtrip {
  return {
    id,
    stops: [],
    returnToStart: false,
    preferences: settings ? { ...settings.preferences } : { winding: DEFAULT_PREFERENCE, motorways: DEFAULT_PREFERENCE, trafficLights: DEFAULT_PREFERENCE },
    extraMinutes: settings?.extraMinutes ?? DEFAULT_EXTRA_MINUTES,
    returnRouting: settings ? { ...settings.returnRouting } : { ...DEFAULT_RETURN_ROUTING }
  };
}

export function newTrip(id: string, name: string, subtripId: string, start?: TripLocation): Trip {
  return { id, name, ...(start ? { start: { ...start, id: 'start' } } : {}), subtrips: [newSubtrip(subtripId)] };
}

/** The next "Trip N" name no trip has, ignoring case and surrounding spaces like [nameTaken]. */
export function nextTripName(names: string[]): string {
  const taken = new Set(names.map((name) => name.trim().toLocaleLowerCase()));
  let number = names.length + 1;
  while (taken.has(`trip ${number}`)) number++;
  return `Trip ${number}`;
}

/** Whether another trip already has this name, ignoring case and surrounding spaces. */
export function nameTaken(trips: Array<Pick<Trip, 'id' | 'name'>>, id: string, name: string): boolean {
  const wanted = name.trim().toLocaleLowerCase();
  return trips.some((trip) => trip.id !== id && trip.name.trim().toLocaleLowerCase() === wanted);
}

export function untitledFallback(name: string): string {
  return name.trim().slice(0, MAX_TRIP_NAME).trim() || UNTITLED;
}

/** Replaces a place without changing its identity or position in the list. */
export function replaceStop(stops: TripLocation[], id: string, replacement: Omit<TripLocation, 'id'>): TripLocation[] {
  return stops.map((stop) => stop.id === id ? { ...replacement, id: stop.id } : stop);
}

/** The ordered points a sub-trip is planned through; a return to the start ends at the start again. */
export function routePoints({ start, destination, stops, returnToStart }: { start?: TripLocation; destination?: TripLocation; stops: TripLocation[]; returnToStart: boolean }): PlannedPoint[] {
  if (!start) return [];
  const point = ({ id, label, lat, lng, source }: TripLocation): PlannedPoint => ({ id, label, lat, lng, source });
  const points = [{ ...point(start), id: 'start' }, ...stops.map(point)];
  if (returnToStart) return [...points, { ...point(start), id: 'return' }];
  if (destination) return [...points, point(destination)];
  return points;
}

/** A return to the start with no stop in between: there is no loop to plan. */
export function needsLoopStop(points: PlannedPoint[]): boolean {
  const [first, second] = points;
  return points.length === 2 && first !== undefined && second !== undefined &&
    Math.abs(first.lat - second.lat) < 0.000001 && Math.abs(first.lng - second.lng) < 0.000001;
}

/** Where sub-trip [index] starts: the trip's start, or where the previous sub-trip ended. */
export function subtripStart(trip: Trip, index: number): TripLocation | undefined {
  return index <= 0 ? trip.start : subtripEnd(trip, index - 1);
}

/** Where sub-trip [index] ends: its start when it returns there, else its destination. */
export function subtripEnd(trip: Trip, index: number): TripLocation | undefined {
  const sub = trip.subtrips[index];
  if (!sub) return undefined;
  return sub.returnToStart ? subtripStart(trip, index) : sub.destination;
}

/** The ordered points sub-trip [index] is planned through. */
export function subtripPoints(trip: Trip, index: number): PlannedPoint[] {
  const sub = trip.subtrips[index]!;
  return routePoints({ start: subtripStart(trip, index), destination: sub.destination, stops: sub.stops, returnToStart: sub.returnToStart });
}

/** Why a sub-trip cannot be planned yet. */
export type NotPlannable = 'NO_START' | 'NO_END' | 'LOOP_NEEDS_STOP' | 'TOO_MANY_POINTS';

/** The plan request for sub-trip [index], or why it cannot be planned yet. */
export function subtripPlanRequest(trip: Trip, index: number, maxPoints: number, maxExtraMinutes: number): { request: PlanRequest } | { reason: NotPlannable } {
  const sub = trip.subtrips[index]!;
  const points = subtripPoints(trip, index);
  if (points.length === 0) return { reason: 'NO_START' };
  if (!sub.returnToStart && !sub.destination) return { reason: 'NO_END' };
  if (needsLoopStop(points)) return { reason: 'LOOP_NEEDS_STOP' };
  if (points.length > maxPoints) return { reason: 'TOO_MANY_POINTS' };
  return { request: { points, preferences: sub.preferences, extraMinutes: Math.min(sub.extraMinutes, maxExtraMinutes), returnRouting: sub.returnRouting } };
}

/** What a plan depends on: the places' coordinates and the settings, not their names. Server-side only; clients read `current` from the API. */
export function planKey(request: PlanRequest): string {
  const { preferences: p, returnRouting: r } = request;
  return JSON.stringify([request.points.map(({ lat, lng }) => [lat, lng]), [p.winding, p.motorways, p.trafficLights], request.extraMinutes, [r?.preference, r?.nearStartKm]]);
}

/** The id the phone knows sub-trip [subtripId] by. */
export function rideTripId(tripId: string, subtripId: string): string {
  return `${tripId}_${subtripId}`;
}

/**
 * Splits sub-trip [index] at one of its stops: that stop becomes its
 * destination, and a new sub-trip [newId], starting there, takes the later
 * stops and the old end (destination, or the old start when it returned there)
 * with a copy of the settings. Unchanged when the stop is not found or the
 * trip already has [MAX_TOUR_SUBTRIPS].
 */
export function splitSubtrip(trip: Trip, index: number, stopId: string, newId: string): Trip {
  const sub = trip.subtrips[index];
  const at = sub?.stops.findIndex((stop) => stop.id === stopId) ?? -1;
  if (!sub || at < 0 || trip.subtrips.length >= MAX_TOUR_SUBTRIPS) return trip;
  const old = subtripEnd(trip, index);
  const stop = sub.stops[at]!;
  const later: Subtrip = {
    ...newSubtrip(newId, sub),
    stops: sub.stops.slice(at + 1),
    ...(old ? { destination: { ...old, id: 'destination' } } : {})
  };
  const first: Subtrip = { ...sub, stops: sub.stops.slice(0, at), destination: { ...stop, id: 'destination' }, returnToStart: false };
  return { ...trip, subtrips: [...trip.subtrips.slice(0, index), first, later, ...trip.subtrips.slice(index + 1)] };
}

/** Whether sub-trip [index] can merge with the next without passing [maxPoints]. */
export function canMerge(trip: Trip, index: number, maxPoints: number): boolean {
  if (index < 0 || index + 1 >= trip.subtrips.length) return false;
  const [first, second] = [trip.subtrips[index]!, trip.subtrips[index + 1]!];
  return 1 + first.stops.length + 1 + second.stops.length + 1 <= maxPoints;
}

/**
 * Merges sub-trip [index] with the next: the first one's end becomes a stop
 * with id [joinStopId], the next one's stops follow, and its end is the
 * destination. The first one's id and settings are kept.
 */
export function mergeWithNext(trip: Trip, index: number, joinStopId: string): Trip {
  if (index < 0 || index + 1 >= trip.subtrips.length) return trip;
  const [first, second] = [trip.subtrips[index]!, trip.subtrips[index + 1]!];
  const join = subtripEnd(trip, index);
  const end = subtripEnd(trip, index + 1);
  const merged: Subtrip = {
    id: first.id,
    stops: [...first.stops, ...(join ? [{ ...join, id: joinStopId }] : []), ...second.stops],
    returnToStart: false,
    preferences: first.preferences,
    extraMinutes: first.extraMinutes,
    returnRouting: first.returnRouting,
    ...(end ? { destination: { ...end, id: 'destination' } } : {})
  };
  return { ...trip, subtrips: [...trip.subtrips.slice(0, index), merged, ...trip.subtrips.slice(index + 2)] };
}

/** Whether a sub-trip can follow the last: it needs somewhere to start. */
export function canAddSubtrip(trip: Trip): boolean {
  return trip.subtrips.length < MAX_TOUR_SUBTRIPS && subtripEnd(trip, trip.subtrips.length - 1) !== undefined;
}

/** Adds an empty sub-trip [newId] after the last, with the last one's settings. */
export function addSubtrip(trip: Trip, newId: string): Trip {
  if (!canAddSubtrip(trip)) return trip;
  return { ...trip, subtrips: [...trip.subtrips, newSubtrip(newId, trip.subtrips.at(-1)!)] };
}

/**
 * Removes sub-trip [index]. The next one then starts where the previous one
 * ended (or, for the first, where the removed one ended). A trip keeps at least one.
 */
export function deleteSubtrip(trip: Trip, index: number): Trip {
  if (trip.subtrips.length < 2 || !trip.subtrips[index]) return trip;
  const next: Trip = { ...trip, subtrips: trip.subtrips.filter((_sub, position) => position !== index) };
  if (index === 0) {
    const start = subtripEnd(trip, 0);
    if (start) next.start = { ...start, id: 'start' };
    else delete next.start;
  }
  return next;
}
